import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero.jsx';
import SafetyNotice from '../components/SafetyNotice.jsx';
import ResourceList from '../components/ResourceList.jsx';
import { FadeUp } from '../components/Motion.jsx';
import { submitReport, openMailClient, PV_EMAIL } from '../utils/aeReport.js';

const AE_FORM = 'https://blp.soltest.de/fileadmin/user_upload/PV004_-_Form_-_AE_Form.pdf';
const PV_FAX = '+49 3641 519 5334';

const initialState = {
  name: '', status: '', hospital: '', department: '', address: '', zip: '', city: '',
  phone: '', email: '', fax: '',
  indication: '', dose: '', injectionDate: '', batchNumber: '', illuminationDate: '',
  treatingCenter: '', country: '', physician: '',
  eventStartDate: '', eventEndDate: '', description: '',
  patientInitials: '', patientAge: '', patientGender: '',
  website: '',
};

const reporterFields = [
  { name: 'name', label: 'Name', required: true, autoComplete: 'name' },
  { name: 'status', label: 'Status (position, patient, other)' },
  { name: 'hospital', label: 'University / hospital', required: true, autoComplete: 'organization' },
  { name: 'department', label: 'Unit / department' },
  { name: 'address', label: 'Address', autoComplete: 'street-address' },
  { name: 'zip', label: 'ZIP / postal code', autoComplete: 'postal-code' },
  { name: 'city', label: 'City', autoComplete: 'address-level2' },
  { name: 'phone', label: 'Phone number', type: 'tel', autoComplete: 'tel' },
  { name: 'email', label: 'Email', required: true, type: 'email', autoComplete: 'email' },
  { name: 'fax', label: 'Fax number' },
];

const productFields = [
  { name: 'indication', label: 'Indication', required: true },
  { name: 'dose', label: 'Dose' },
  { name: 'injectionDate', label: 'Date of Foscan\u00ae injection', required: true, type: 'date' },
  { name: 'batchNumber', label: 'Batch number of Foscan\u00ae' },
  { name: 'illuminationDate', label: 'Date of illumination', type: 'date' },
  { name: 'treatingCenter', label: 'Treating centre', required: true },
  { name: 'country', label: 'Country of treating centre', required: true },
  { name: 'physician', label: 'Treating physician', required: true },
];

const patientFields = [
  { name: 'patientInitials', label: 'Initials (given name / surname)', required: true },
  { name: 'patientAge', label: 'Age', required: true },
  { name: 'patientGender', label: 'Gender', required: true },
];

const resources = [
  { label: 'Adverse event report form', meta: 'PDF \u2014 for email or fax submission', href: AE_FORM },
];

function Field({ field, value, onChange }) {
  const { name, label, required, type = 'text', autoComplete } = field;
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={name}>
        {label}
        {required && <span className="form-req"> (required)</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        aria-required={required || undefined}
        className="form-input"
      />
    </div>
  );
}

export default function AdverseEventReport() {
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [invalidCount, setInvalidCount] = useState(0);
  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* onInvalid bubbles in React, so one handler on the form catches every
     field. Counting is deferred a frame so the browser has finished marking
     all invalid controls before we query them. */
  const handleInvalid = () => {
    window.requestAnimationFrame(() => {
      const node = formRef.current;
      if (node) setInvalidCount(node.querySelectorAll(':invalid').length);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.website) return; // honeypot

    setInvalidCount(0);
    setStatus('sending');
    try {
      await submitReport(form);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <>
      <PageHero
        eyebrow="PHARMACOVIGILANCE"
        title="Report an adverse event"
        subtitle="Report an adverse event experienced during treatment with Foscan&reg;."
      />

      <div className="page-shell">
        <div className="page-grid">
          <div className="page-main">
            <FadeUp>
              <section className="page-section" id="how-to-report">
                <h2 className="page-section-title">How to report</h2>
                <p className="page-body">
                  You can report an adverse event using the form on this page, or by
                  downloading the detailed report form and sending it to{' '}
                  <a href={`mailto:${PV_EMAIL}`}>{PV_EMAIL}</a> or to fax number {PV_FAX}.
                </p>
                <p className="page-body">
                  In any case, we will get in touch with you as soon as possible.
                </p>
              </section>
            </FadeUp>

            {status === 'sent' ? (
              <FadeUp>
                <div className="form-success" role="status" aria-live="polite">
                  <p>Thank you &mdash; your report has been submitted to our pharmacovigilance team.</p>
                  <p>We will get in touch with you as soon as possible.</p>
                </div>
              </FadeUp>
            ) : (
              <form
                ref={formRef}
                className="form-card"
                onSubmit={handleSubmit}
                onInvalid={handleInvalid}
              >
                <div className="form-consent">
                  <h2 className="form-consent-title">How we use this information</h2>
                  <p>
                    The information you submit is processed by biolitec Pharma Ltd. to
                    meet our legal pharmacovigilance obligations under EU law. It is
                    stored on servers within the European Union and is shared with health
                    authorities where we are required to do so.
                  </p>
                  <p>
                    Please provide patient initials and age only &mdash; do not include
                    full names, addresses or other directly identifying patient details.
                  </p>
                  <p>
                    See our <Link to="/privacy-policy">privacy policy</Link> for how we
                    handle personal data and how to exercise your rights.
                  </p>
                </div>

                <fieldset className="form-fieldset">
                  <legend className="form-legend">Reporter details</legend>
                  <div className="form-grid">
                    {reporterFields.map((f) => (
                      <Field key={f.name} field={f} value={form[f.name]} onChange={handleChange} />
                    ))}
                  </div>
                </fieldset>

                <fieldset className="form-fieldset">
                  <legend className="form-legend">Product details</legend>
                  <p className="form-note">Product name: Foscan&reg;</p>
                  <div className="form-grid">
                    {productFields.map((f) => (
                      <Field key={f.name} field={f} value={form[f.name]} onChange={handleChange} />
                    ))}
                  </div>
                </fieldset>

                <fieldset className="form-fieldset">
                  <legend className="form-legend">Details of the adverse event</legend>
                  <div className="form-grid">
                    <Field
                      field={{ name: 'eventStartDate', label: 'Start date', type: 'date' }}
                      value={form.eventStartDate}
                      onChange={handleChange}
                    />
                    <Field
                      field={{ name: 'eventEndDate', label: 'End date', type: 'date' }}
                      value={form.eventEndDate}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-field form-field--full">
                    <label className="form-label" htmlFor="description">
                      Description, including signs, symptoms, treatment and relevant lab data
                      <span className="form-req"> (required)</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={5}
                      required
                      aria-required="true"
                      value={form.description}
                      onChange={handleChange}
                      className="form-input form-textarea"
                    />
                  </div>
                </fieldset>

                <fieldset className="form-fieldset">
                  <legend className="form-legend">Patient details</legend>
                  <p className="form-note">
                    Initials and age only. Do not enter the patient&rsquo;s full name.
                  </p>
                  <div className="form-grid">
                    {patientFields.map((f) => (
                      <Field key={f.name} field={f} value={form[f.name]} onChange={handleChange} />
                    ))}
                  </div>
                </fieldset>

                <div className="form-honeypot" aria-hidden="true">
                  <label htmlFor="website">Do not fill this field</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={handleChange}
                  />
                </div>

                {invalidCount > 0 && (
                  <div className="form-error" role="alert">
                    <p>
                      {invalidCount === 1
                        ? 'One required field still needs completing.'
                        : `${invalidCount} required fields still need completing.`}{' '}
                      Please check the fields marked by your browser above.
                    </p>
                  </div>
                )}

                <div className="form-actions">
                  <button type="submit" className="medinfo-btn" disabled={status === 'sending'}>
                    {status === 'sending' ? 'Sending\u2026' : 'Submit report'}
                  </button>
                  <span className="form-required-note">Fields marked (required) must be completed</span>
                </div>

                {status === 'error' && (
                  <div className="form-error" role="alert">
                    <p>
                      The report could not be sent automatically.{' '}
                      <button type="button" className="link-btn" onClick={() => openMailClient(form)}>
                        Open in your email client
                      </button>{' '}
                      or email the details to <a href={`mailto:${PV_EMAIL}`}>{PV_EMAIL}</a>.
                    </p>
                  </div>
                )}
              </form>
            )}
          </div>

          <div className="page-aside">
            <SafetyNotice title="Why reporting matters">
              <p>
                Reporting adverse events helps us monitor the safety of Foscan&reg; in
                clinical use and meet our regulatory obligations.
              </p>
            </SafetyNotice>
            <ResourceList items={resources} />
          </div>
        </div>
      </div>
    </>
  );
}